import { forwardRef, ReactNode } from 'react';

/**
 * Hi‑Fi design form primitives. Each renders a raw HTML element with the
 * matching `.input` / `.select` / `.textarea` / `.f-label` / `.btn` class so
 * the design's `assets/ali-shell.css` (now ported to
 * `frontend/src/styles/edit-object-design.css`) drives the visuals.
 *
 * These primitives stay deliberately thin — no MUI, no theming, no
 * complicated logic. Sections compose them; the design CSS does the rest.
 *
 * Usage: only valid inside the `.ali-edit-object` scope (rendered by
 * `<EditObjectShell>`); the styles cascade only there.
 */

type DivProps = React.HTMLAttributes<HTMLDivElement>;

interface FLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
  hint?: ReactNode;
  children: ReactNode;
}

export const FLabel = ({
  required,
  hint,
  children,
  className,
  ...rest
}: FLabelProps) => (
  <label className={`f-label${className ? ` ${className}` : ''}`} {...rest}>
    {children}
    {required && <span className="req">*</span>}
    {hint && <span className="hint"> {hint}</span>}
  </label>
);

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Larger 16px variant used for primary text fields (e.g. Title). */
  big?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ big, className, ...rest }, ref) => (
    <input
      ref={ref}
      className={`input${big ? ' big' : ''}${className ? ` ${className}` : ''}`}
      {...rest}
    />
  ),
);
Input.displayName = 'Input';

type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, ...rest }, ref) => (
    <select
      ref={ref}
      className={`select${className ? ` ${className}` : ''}`}
      {...rest}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';

type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...rest }, ref) => (
    <textarea
      ref={ref}
      className={`textarea${className ? ` ${className}` : ''}`}
      {...rest}
    />
  ),
);
Textarea.displayName = 'Textarea';

interface BtnProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: 'primary' | 'outlined' | 'ghost';
  type?: 'button' | 'submit' | 'reset';
}

export const Btn = ({
  variant = 'primary',
  className,
  type = 'button',
  children,
  ...rest
}: BtnProps) => (
  <button
    type={type}
    className={`btn btn-${variant}${className ? ` ${className}` : ''}`}
    {...rest}
  >
    {children}
  </button>
);

/** Pure layout helpers matching the design's grid utilities. */
export const Two = ({ children, ...rest }: DivProps) => (
  <div className="two" {...rest}>
    {children}
  </div>
);
export const Three = ({ children, ...rest }: DivProps) => (
  <div className="three" {...rest}>
    {children}
  </div>
);
export const Row = ({ children, ...rest }: DivProps) => (
  <div className="row" {...rest}>
    {children}
  </div>
);
